import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Menu } from 'lucide-react';

const SERVICES = [
  { label: 'Wedding', path: '/wedding' },
  { label: 'Fashion', path: '/fashion' },
  { label: 'Events', path: '/events' },
  { label: 'Commercial', path: '/commercial' },
];

export default function Navbar({ onMenuClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;
  const isServiceActive = SERVICES.some(s => location.pathname === s.path) || location.pathname === '/services';

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'navbar-solid py-3' : 'navbar-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" data-testid="navbar-logo" className="flex items-baseline gap-0 group">
          <span className="font-heading text-gold text-3xl sm:text-3xl italic tracking-[0.15em] group-hover:text-gold-light transition-colors duration-300">V</span>
          <span className="font-heading text-visyra-text text-2xl sm:text-2xl tracking-[0.15em] group-hover:text-gold-light transition-colors duration-300">isyra</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-10">
          <Link
            to="/"
            data-testid="nav-link-home"
            className={`font-body text-sm font-medium uppercase tracking-widest transition-colors duration-300 ${
              isActive('/') ? 'text-gold' : 'text-visyra-muted hover:text-gold'
            }`}
          >
            Home
          </Link>

          {/* Services Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => { clearTimeout(timeoutRef.current); setDropdownOpen(true); }}
            onMouseLeave={() => { timeoutRef.current = setTimeout(() => setDropdownOpen(false), 200); }}
          >
            <Link
              to="/services"
              data-testid="nav-link-services"
              className={`font-body text-sm font-medium uppercase tracking-widest transition-colors duration-300 flex items-center gap-1 ${
                isServiceActive ? 'text-gold' : 'text-visyra-muted hover:text-gold'
              }`}
            >
              Services
              <ChevronDown size={14} className={`transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`} />
            </Link>

            <AnimatePresence>
              {dropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  data-testid="services-dropdown"
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-52 bg-visyra-paper/95 backdrop-blur-md border border-white/10 rounded-sm overflow-hidden"
                >
                  {SERVICES.map((s) => (
                    <Link
                      key={s.path}
                      to={s.path}
                      onClick={() => setDropdownOpen(false)}
                      data-testid={`dropdown-${s.label.toLowerCase()}`}
                      className={`block px-5 py-3 font-body text-sm tracking-wide border-l-2 transition-all duration-300 ${
                        isActive(s.path)
                          ? 'text-gold border-gold bg-gold-subtle'
                          : 'text-visyra-muted border-transparent hover:text-gold hover:border-gold hover:bg-gold-subtle'
                      }`}
                    >
                      {s.label}
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link
            to="/about"
            data-testid="nav-link-about"
            className={`font-body text-sm font-medium uppercase tracking-widest transition-colors duration-300 ${
              isActive('/about') ? 'text-gold' : 'text-visyra-muted hover:text-gold'
            }`}
          >
            About Us
          </Link>

          <Link
            to="/contact"
            data-testid="nav-link-contact"
            className={`font-body text-sm font-medium uppercase tracking-widest transition-colors duration-300 ${
              isActive('/contact') ? 'text-gold' : 'text-visyra-muted hover:text-gold'
            }`}
          >
            Contact Us
          </Link>

          <Link
            to="/contact"
            data-testid="book-now-btn"
            className="btn-shimmer font-body text-sm font-medium uppercase tracking-widest text-gold border border-gold px-6 py-2.5 hover:bg-gold hover:text-visyra-bg transition-all duration-400"
          >
            Book Now
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={onMenuClick}
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-visyra-text hover:text-gold transition-colors duration-300"
          aria-label="Open menu"
        >
          <Menu size={26} />
        </button>
      </div>
    </nav>
  );
}
